import React from 'react';
type MarkProps = {
  className?: string;
};

/** Handball: a round ball with the typical curved panel seams. */
export function BallMark({
  className = ''
}: MarkProps) {
  return <svg viewBox="0 0 48 48" className={className} aria-hidden="true" focusable="false" data-id="element-118" data-mp="11jrd4re80q5d">
      <circle cx="24" cy="24" r="19" fill="none" stroke="currentColor" strokeWidth="2.5" data-id="element-119" data-mp="16hsvfa3tolfs" />
      <path d="M24 5c-6 6-6 13 0 19s6 13 0 19" fill="none" stroke="currentColor" strokeWidth="2" data-id="element-120" data-mp="1u8y85geguxlv" />
      <path d="M5 24c6-5 13-5 19 0s13 5 19 0" fill="none" stroke="currentColor" strokeWidth="2" data-id="element-121" data-mp="1ka91kjs0szkx" />
    </svg>;
}

/** Handball goal seen from the front. */
export function GoalMark({
  className = ''
}: MarkProps) {
  return <svg viewBox="0 0 48 48" className={className} aria-hidden="true" focusable="false" data-id="element-122" data-mp="8tiukyfrw3q9">
      <rect x="6" y="14" width="36" height="24" rx="2" fill="none" stroke="currentColor" strokeWidth="2.5" data-id="element-123" data-mp="drkcvh5djz0o" />
      <path d="M15 14v24M24 14v24M33 14v24M6 22h36M6 30h36" stroke="currentColor" strokeWidth="1" opacity="0.6" data-id="element-124" data-mp="2ddh4ul5fqguv" />
    </svg>;
}

/** Indoor court seen from above, with both goal areas. */
export function CourtMark({
  className = ''
}: MarkProps) {
  return <svg viewBox="0 0 64 40" className={className} aria-hidden="true" focusable="false" data-id="element-125" data-mp="30aan2vzbvbai">
      <rect x="2" y="2" width="60" height="36" rx="2" fill="none" stroke="currentColor" strokeWidth="2" data-id="element-126" data-mp="2w8pa7amsmx0b" />
      <path d="M32 2v36" stroke="currentColor" strokeWidth="1.5" data-id="element-127" data-mp="353ltiw089q1w" />
      <path d="M2 10a12 12 0 0 1 0 20M62 10a12 12 0 0 0 0 20" fill="none" stroke="currentColor" strokeWidth="1.5" data-id="element-128" data-mp="3k15ulru0712s" />
      <path d="M2 16v8M62 16v8" stroke="currentColor" strokeWidth="3" data-id="element-129" data-mp="3ez6qugucq25b" />
    </svg>;
}

/**
 * Schematic half court used as the editorial motif on the band covers.
 * `variant` slightly changes the drill markings so the six covers differ.
 */
export function CourtMotif({
  variant = 1,
  className = ''
}: MarkProps & {
  variant?: number;
}) {
  const markings: Record<number, React.ReactNode> = {
    1: <>
        <circle cx="30" cy="70" r="4" fill="currentColor" data-id="element-130" data-mp="9n2v2jhzve20" />
        <circle cx="70" cy="56" r="4" fill="currentColor" data-id="element-131" data-mp="9mjbkycp4hlv" />
        <path d="M34 68 66 58" stroke="currentColor" strokeWidth="2" strokeDasharray="5 5" data-id="element-132" data-mp="dnmrfkuqudn3" />
      </>,
    2: <>
        <circle cx="26" cy="64" r="4" fill="currentColor" data-id="element-133" data-mp="nmc0qp8kqprq" />
        <circle cx="50" cy="78" r="4" fill="currentColor" data-id="element-134" data-mp="nlssy1cn1hrf" />
        <circle cx="74" cy="64" r="4" fill="currentColor" data-id="element-135" data-mp="2kfnjgkhtki83" />
        <path d="M30 66 46 76M54 76 70 66" stroke="currentColor" strokeWidth="2" strokeDasharray="5 5" data-id="element-136" data-mp="2kh704c7e0vae" />
      </>,
    3: <>
        <circle cx="28" cy="74" r="4" fill="currentColor" data-id="element-137" data-mp="2hwis4lpvdmbd" />
        <circle cx="72" cy="74" r="4" fill="currentColor" data-id="element-138" data-mp="2d2obxs9h3ph1" />
        <path d="M32 74h36" stroke="currentColor" strokeWidth="2" strokeDasharray="6 5" data-id="element-139" data-mp="284d4tpakbt2i" />
        <path d="M50 66v-18" stroke="currentColor" strokeWidth="2" data-id="element-140" data-mp="236l5493of2nr" />
        <path d="M46 52 50 44 54 52" fill="none" stroke="currentColor" strokeWidth="2" data-id="element-141" data-mp="30t0mlaw3s0bb" />
      </>,
    4: <>
        <circle cx="34" cy="76" r="4" fill="currentColor" data-id="element-142" data-mp="2vscusn01vfg1" />
        <path d="M46 72h10M51 67v10" stroke="currentColor" strokeWidth="2" data-id="element-143" data-mp="2qtt64eu2npjz" />
        <path d="M38 74c10-4 16-12 16-22" fill="none" stroke="currentColor" strokeWidth="2" strokeDasharray="5 5" data-id="element-144" data-mp="2lwszo2tb8ksc" />
        <path d="M50 48 54 52 58 46" fill="none" stroke="currentColor" strokeWidth="2" data-id="element-145" data-mp="1agulxln6taks" />
      </>,
    5: <>
        <circle cx="50" cy="78" r="4" fill="currentColor" data-id="element-146" data-mp="2z9qqv5gmzu4m" />
        <path d="M38 62h10M43 57v10M62 62h10M67 57v10" stroke="currentColor" strokeWidth="2" data-id="element-147" data-mp="342tqnvc0bzt9" />
        <path d="M50 74c0-12 6-20 16-24" fill="none" stroke="currentColor" strokeWidth="2" strokeDasharray="5 5" data-id="element-148" data-mp="2pdnjnl6xh5at" />
      </>,
    6: <>
        <circle cx="24" cy="70" r="4" fill="currentColor" data-id="element-149" data-mp="3i2l1jn789031" />
        <circle cx="50" cy="60" r="4" fill="currentColor" data-id="element-150" data-mp="3n0mfjtpsq97n" />
        <circle cx="76" cy="70" r="4" fill="currentColor" data-id="element-151" data-mp="3n04emjs6v6te" />
        <path d="M28 68 46 62M54 62 72 68M50 54V40" stroke="currentColor" strokeWidth="2" strokeDasharray="5 5" data-id="element-152" data-mp="383vivs7ibblq" />
        <path d="M46 44 50 36 54 44" fill="none" stroke="currentColor" strokeWidth="2" data-id="element-153" data-mp="3d49q581aahip" />
      </>
  };
  return <svg viewBox="0 0 100 96" className={className} aria-hidden="true" focusable="false" data-id="element-154" data-mp="21o30b16v4610">
      <rect x="4" y="4" width="92" height="88" rx="3" fill="none" stroke="currentColor" strokeWidth="2" opacity="0.5" data-id="element-155" data-mp="jntxcq5z6qgr" />
      {/* goal line and goal area */}
      <path d="M38 4h24" stroke="currentColor" strokeWidth="4" data-id="element-156" data-mp="op14cn0anlmd" />
      <path d="M20 4a30 30 0 0 0 60 0" fill="none" stroke="currentColor" strokeWidth="2" opacity="0.7" data-id="element-157" data-mp="omn84atvo3d8" />
      <path d="M26 4a24 24 0 0 0 48 0" fill="none" stroke="currentColor" strokeWidth="1.5" strokeDasharray="4 4" opacity="0.5" data-id="element-158" data-mp="3s69j2xzq65cv" />
      {markings[variant] ?? markings[1]}
    </svg>;
}